package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDownward
import androidx.compose.material.icons.filled.ArrowUpward
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.tooling.preview.PreviewScreenSizes
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.presentation.preview.MonthlyBreakdownPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import kotlin.math.abs

@Composable
fun MonthlyBreakdownScreen(
    yearMonth: String,
    onNavigateBack: () -> Unit,
    onTransactionClick: (String, String?) -> Unit,
    viewModel: MonthlyBreakdownViewModel = koinViewModel(key = yearMonth) {
        parametersOf(yearMonth)
    }
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    MonthlyBreakdownScreenContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onTransactionClick = onTransactionClick,
        onRefresh = viewModel::refresh
    )
}

@Composable
fun MonthlyBreakdownScreenContent(
    state: MonthlyBreakdownScreenState,
    onNavigateBack: () -> Unit,
    onTransactionClick: (String, String?) -> Unit,
    onRefresh: () -> Unit = {}
) {
    val title = when (state) {
        is MonthlyBreakdownScreenState.Loaded -> "${state.yearMonth} Breakdown"
        else -> "Monthly Breakdown"
    }

    Scaffold(
        modifier = Modifier.fillMaxSize(),
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = title,
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                }
            )
        }
    ) { innerPadding ->
        when (state) {
            is MonthlyBreakdownScreenState.Loading -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(
                        color = MaterialTheme.colorScheme.primary
                    )
                }
            }
            is MonthlyBreakdownScreenState.Error -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding)
                        .padding(24.dp),
                    contentAlignment = Alignment.Center
                ) {
                    GlassCard(modifier = Modifier.fillMaxWidth()) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            Text(
                                text = "Unable to load data",
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.error
                            )
                            Text(
                                text = state.message,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                            Button(
                                onClick = onRefresh,
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = MaterialTheme.colorScheme.primary,
                                    contentColor = Color(0xFF003731)
                                )
                            ) {
                                Text("Retry")
                            }
                        }
                    }
                }
            }
            is MonthlyBreakdownScreenState.Loaded -> {
                MonthlyBreakdownList(
                    state = state,
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    onTransactionClick = onTransactionClick
                )
            }
        }
    }
}

@Composable
private fun MonthlyBreakdownList(
    state: MonthlyBreakdownScreenState.Loaded,
    modifier: Modifier = Modifier,
    onTransactionClick: (String, String?) -> Unit
) {
    val greenColor = Color(0xFF4CAF50)
    val redColor = Color(0xFFEF5350)

    LazyColumn(
        modifier = modifier,
        contentPadding = PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        // Month Summary Header
        item(key = "month_summary_header") {
            MonthSummaryCard(state = state)
        }

        // Income Section Header
        item(key = "income_section_header") {
            SectionHeader(
                title = "Income",
                count = state.incomeTransactions.size,
                amountText = "+$${state.totalIncome.formatAmount()}",
                amountColor = greenColor
            )
        }

        if (state.incomeTransactions.isEmpty()) {
            item(key = "empty_income") {
                EmptyTransactionsCard(message = "No income transactions for this month.")
            }
        } else {
            items(state.incomeTransactions, key = { "inc_${it.transactionId}" }) { tx ->
                val dashboardId = state.transactionDashboardIds[tx.transactionId]
                MonthlyTransactionCard(
                    tx = tx,
                    isIncome = true,
                    accountName = state.accountNames[tx.accountId],
                    institutionName = state.institutionNames[tx.accountId],
                    onClick = { onTransactionClick(tx.transactionId, dashboardId) }
                )
            }
        }

        // Expenses Section Header
        item(key = "expenses_section_header") {
            SectionHeader(
                title = "Expenses",
                count = state.expenseTransactions.size,
                amountText = "-$${state.totalExpenses.formatAmount()}",
                amountColor = redColor
            )
        }

        if (state.expenseTransactions.isEmpty()) {
            item(key = "empty_expenses") {
                EmptyTransactionsCard(message = "No expense transactions for this month.")
            }
        } else {
            items(state.expenseTransactions, key = { "exp_${it.transactionId}" }) { tx ->
                val dashboardId = state.transactionDashboardIds[tx.transactionId]
                MonthlyTransactionCard(
                    tx = tx,
                    isIncome = false,
                    accountName = state.accountNames[tx.accountId],
                    institutionName = state.institutionNames[tx.accountId],
                    onClick = { onTransactionClick(tx.transactionId, dashboardId) }
                )
            }
        }
    }
}

@Composable
private fun MonthSummaryCard(
    state: MonthlyBreakdownScreenState.Loaded,
    modifier: Modifier = Modifier
) {
    val greenColor = Color(0xFF4CAF50)
    val redColor = Color(0xFFEF5350)
    val isNetPositive = state.netIncome >= 0

    GlassCard(modifier = modifier.fillMaxWidth()) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Text(
                text = "${state.yearMonth} Summary",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )

            // Primary 2x2 Grid of Key Totals
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                MetricCard(
                    modifier = Modifier.weight(1f),
                    label = "Total Income",
                    value = "+$${state.totalIncome.formatAmount()}",
                    valueColor = greenColor,
                    icon = {
                        Icon(
                            imageVector = Icons.Default.ArrowUpward,
                            contentDescription = null,
                            tint = greenColor,
                            modifier = Modifier.size(16.dp)
                        )
                    }
                )

                MetricCard(
                    modifier = Modifier.weight(1f),
                    label = "Total Expenses",
                    value = "-$${state.totalExpenses.formatAmount()}",
                    valueColor = redColor,
                    icon = {
                        Icon(
                            imageVector = Icons.Default.ArrowDownward,
                            contentDescription = null,
                            tint = redColor,
                            modifier = Modifier.size(16.dp)
                        )
                    }
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                val netPrefix = if (isNetPositive) "+" else "-"
                MetricCard(
                    modifier = Modifier.weight(1f),
                    label = "Net Cash Flow",
                    value = "$netPrefix$${abs(state.netIncome).formatAmount()}",
                    valueColor = if (isNetPositive) greenColor else redColor
                )

                val ratePrefix = if (state.savingsRate >= 0) "+" else ""
                MetricCard(
                    modifier = Modifier.weight(1f),
                    label = "Savings Rate",
                    value = "$ratePrefix${state.savingsRate.formatAmount()}%",
                    valueColor = if (state.savingsRate >= 0) greenColor else redColor
                )
            }

            HorizontalDivider(
                modifier = Modifier.fillMaxWidth(),
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.08f)
            )

            // Expense Ratio Progress Bar
            val spendRatio = if (state.totalIncome > 0) (state.totalExpenses / state.totalIncome).toFloat().coerceIn(0f, 1f) else 1f
            LinearProgressIndicator(
                progress = { spendRatio },
                modifier = Modifier
                    .fillMaxWidth()
                    .height(6.dp)
                    .clip(RoundedCornerShape(3.dp)),
                color = if (spendRatio <= 0.8f) greenColor else if (spendRatio <= 1.0f) Color(0xFFFFB74D) else redColor,
                trackColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.1f)
            )

            Text(
                text = "${state.incomeTransactions.size} income • ${state.expenseTransactions.size} expense transactions",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f)
            )
        }
    }
}

@Composable
private fun MetricCard(
    label: String,
    value: String,
    valueColor: Color,
    modifier: Modifier = Modifier,
    icon: (@Composable () -> Unit)? = null
) {
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(Color(0x10FFFFFF))
            .padding(12.dp)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                if (icon != null) {
                    icon()
                }
                Text(
                    text = label,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Text(
                text = value,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = valueColor,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
private fun SectionHeader(
    title: String,
    count: Int,
    amountText: String,
    amountColor: Color,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .padding(top = 8.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(10.dp))
                    .background(Color(0x15FFFFFF))
                    .padding(horizontal = 8.dp, vertical = 2.dp)
            ) {
                Text(
                    text = "$count",
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }

        Text(
            text = amountText,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Bold,
            color = amountColor
        )
    }
}

@Composable
private fun EmptyTransactionsCard(
    message: String,
    modifier: Modifier = Modifier
) {
    GlassCard(modifier = modifier.fillMaxWidth()) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(20.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = message,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun MonthlyTransactionCard(
    tx: Transaction,
    isIncome: Boolean,
    accountName: String?,
    institutionName: String?,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val greenColor = Color(0xFF4CAF50)
    val redColor = Color(0xFFEF5350)

    val primaryCategory = tx.activeCategory?.detailed?.replace("_", " ")
        ?: tx.activeCategory?.primary?.replace("_", " ")
        ?: tx.category.firstOrNull()?.replace("_", " ")
    val accountDetail = listOfNotNull(accountName, institutionName).joinToString(" • ")
    val secondaryText = listOfNotNull(primaryCategory, accountDetail).filter { it.isNotEmpty() }.joinToString(" • ")

    GlassCard(
        modifier = modifier.fillMaxWidth(),
        onClick = onClick
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(2.dp)
            ) {
                Text(
                    text = tx.merchantName ?: tx.name,
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                if (secondaryText.isNotEmpty()) {
                    Text(
                        text = secondaryText,
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
                Text(
                    text = tx.date,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f)
                )
            }
            
            Text(
                text = "${if (isIncome) "+" else "-"}$${abs(tx.amount).formatAmount()}",
                style = MaterialTheme.typography.bodyLarge,
                fontWeight = FontWeight.Bold,
                color = if (isIncome) greenColor else redColor
            )
        }
    }
}

@PreviewLightDark
@PreviewScreenSizes
@Composable
fun MonthlyBreakdownScreenPreview(
    @PreviewParameter(MonthlyBreakdownPreviewParameterProvider::class) state: MonthlyBreakdownScreenState
) {
    AppTheme(darkTheme = isSystemInDarkTheme(), dynamicColor = false) {
        Surface {
            MonthlyBreakdownScreenContent(
                state = state,
                onNavigateBack = {},
                onTransactionClick = { _, _ -> }
            )
        }
    }
}
